import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Button,
  Card,
  Row,
  Col,
  message,
  Space,
  Typography,
  Select,
  Table,
  Divider,
  Tag,
} from 'antd';
import {
  SaveOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { stockService } from '../../services';
import { formatNumber, debounce } from '../../utils/helpers';

const { TextArea } = Input;
const { Title } = Typography;
const { Option } = Select;

const StockForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [stockData, setStockData] = useState([]); // Dữ liệu tồn kho của tất cả sản phẩm
  const [filteredStockData, setFilteredStockData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [inputValues, setInputValues] = useState({}); // Track input values locally
  const [scrollPosition, setScrollPosition] = useState(0); // Track scroll position
  const navigate = useNavigate();

  useEffect(() => {
    loadStockData();

    // Mobile detection
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const loadStockData = async () => {
    setLoading(true);
    try {
      // Lấy dữ liệu tồn kho từ stock variants API
      const response = await stockService.getVariants();
      //console.log('🔍 StockForm - API response:', response);
      if (response.success) {
        const variants = response.data || [];
        //console.log('🔍 StockForm - variants data:', variants.slice(0, 3)); // Log 3 mẫu đầu tiên
        
        // Chuyển đổi thành format phù hợp cho editing
        const stockData = variants.map((variant, index) => ({
          key: variant.variant_id || index,
          variant_id: variant.variant_id,
          product_id: variant.product_id,
          product_name: variant.product_name,
          variant_name: variant.variant_name,
          sku: variant.sku,
          current_stock: variant.stock || 0, // Sửa từ stock_quantity thành stock
          adjustment_quantity: 0,
          new_stock: variant.stock || 0, // Sửa từ stock_quantity thành stock
          reason: '',
          changed: false // Đánh dấu sản phẩm đã thay đổi
        }));
        //console.log('🔍 StockForm - processed stockData:', stockData.slice(0, 3)); // Log 3 mẫu đã xử lý
        setStockData(stockData);
        setFilteredStockData(stockData);
      }
    } catch (error) {
      //console.error('Error loading stock data:', error);
      message.error('Không thể tải dữ liệu tồn kho');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = debounce((value) => {
    setSearchText(value);
    if (!value.trim()) {
      setFilteredStockData(stockData);
    } else {
      const filtered = stockData.filter(item =>
        item.product_name?.toLowerCase().includes(value.toLowerCase()) ||
        item.variant_name?.toLowerCase().includes(value.toLowerCase()) ||
        item.sku?.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredStockData(filtered);
    }
  }, 300);

  const handleStockChange = useCallback((key, field, value) => {
    // Save current scroll position before update
    const scrollContainer = document.querySelector('.stock-scroll-container');
    if (scrollContainer) {
      setScrollPosition(scrollContainer.scrollTop);
    }

    setStockData(prevStockData => {
      const newStockData = prevStockData.map(item => {
        if (item.key === key) {
          const updatedItem = { ...item, [field]: value, changed: true };
          
          // Tự động tính toán new_stock khi thay đổi adjustment_quantity
          if (field === 'adjustment_quantity') {
            updatedItem.new_stock = updatedItem.current_stock + (value || 0);
          }
          
          return updatedItem;
        }
        return item;
      });
      
      // Update filtered data without causing scroll jump
      requestAnimationFrame(() => {
        if (searchText.trim()) {
          const filtered = newStockData.filter(item =>
            item.product_name?.toLowerCase().includes(searchText.toLowerCase()) ||
            item.variant_name?.toLowerCase().includes(searchText.toLowerCase()) ||
            item.sku?.toLowerCase().includes(searchText.toLowerCase())
          );
          setFilteredStockData(filtered);
        } else {
          setFilteredStockData(newStockData);
        }

        // Restore scroll position
        requestAnimationFrame(() => {
          const scrollContainer = document.querySelector('.stock-scroll-container');
          if (scrollContainer) {
            scrollContainer.scrollTop = scrollPosition;
          }
        });
      });
      
      return newStockData;
    });
  }, [searchText, scrollPosition]);

  // Separate handler for input changes to prevent focus loss
  const handleInputChange = useCallback((key, field, value) => {
    // Update local input values immediately - NO data update here
    setInputValues(prev => ({
      ...prev,
      [`${key}_${field}`]: value
    }));
  }, []);

  // Separate function to sync data on blur or after delay
  const syncDataUpdate = useCallback((key, field, value) => {
    handleStockChange(key, field, value);
  }, [handleStockChange]);

  // Remove the problematic debounced function and useEffect
  // const debouncedUpdateStock = useMemo(...);
  // useEffect for debounced updates - REMOVED

  const handleSubmit = async (values) => {
    // Chỉ điều chỉnh những sản phẩm đã thay đổi
    const changedProducts = stockData.filter(item => 
      item.changed && 
      item.adjustment_quantity !== 0 && 
      item.reason.trim()
    );

    if (changedProducts.length === 0) {
      message.warning('Vui lòng điều chỉnh ít nhất một sản phẩm và nhập lý do');
      return;
    }

    setLoading(true);
    try {
      // Gửi từng adjustment request
      const adjustmentPromises = changedProducts.map(item => {
        return stockService.update(item.variant_id, {
          accessToken: localStorage.getItem('accessToken'),
          newStock: item.new_stock,
          changeType: 'manual',
          note: `${values.general_reason ? values.general_reason + ' - ' : ''}${item.reason}`,
        });
      });

      await Promise.all(adjustmentPromises);
      
      message.success(`Điều chỉnh thành công ${changedProducts.length} sản phẩm`);
      navigate('/stock/list');
    } catch (error) {
      //console.error('Error adjusting stock:', error);
      message.error('Điều chỉnh tồn kho thất bại');
    } finally {
      setLoading(false);
    }
  };

  // Mobile Variant Card Component
  const MobileVariantCard = React.memo(({ record, onInputChange, onDataSync }) => {
    const [localAdjustment, setLocalAdjustment] = React.useState(record.adjustment_quantity);
    const [localReason, setLocalReason] = React.useState(record.reason);

    const getStatusInfo = () => {
      if (localAdjustment === 0) {
        return { text: 'Không đổi', color: '#999' };
      }
      if (!localReason || !localReason.toString().trim()) {
        return { text: 'Thiếu lý do', color: '#ff4d4f' };
      }
      return { text: 'Sẵn sàng', color: '#52c41a' };
    };

    const statusInfo = getStatusInfo();

    return (
      <Card 
        size="small" 
        style={{ 
          marginBottom: 12,
          borderLeft: record.changed ? '4px solid #1890ff' : '4px solid transparent'
        }}
      >
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontWeight: 'bold', color: '#1890ff', marginBottom: 4 }}>
            {record.product_name}
          </div>
          {record.variant_name && record.variant_name !== record.product_name && (
            <div style={{ color: '#333', fontSize: 13, marginBottom: 2 }}>
              📦 {record.variant_name}
            </div>
          )}
          <div style={{ fontSize: 11, color: '#999' }}>
            <Tag size="small" color="blue">SKU: {record.sku}</Tag>
            <Tag size="small">ID: {record.variant_id}</Tag>
          </div>
        </div>

        <Row gutter={8} style={{ marginBottom: 12 }}>
          <Col span={8}>
            <div style={{ textAlign: 'center', padding: 8, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
              <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>Tồn kho hiện tại</div>
              <div style={{ fontWeight: 'bold', color: '#333' }}>
                {formatNumber(record.current_stock)}
              </div>
            </div>
          </Col>
          <Col span={8}>
            <div style={{ textAlign: 'center', padding: 8, backgroundColor: '#f0f9ff', borderRadius: 4 }}>
              <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>Điều chỉnh</div>
              <InputNumber
                value={localAdjustment}
                onChange={(value) => {
                  // Set local state immediately for smooth typing
                  setLocalAdjustment(value === null ? '' : value);
                }}
                onBlur={(e) => {
                  // Parse and sync data only onBlur
                  const numValue = parseFloat(e.target.value) || 0;
                  setLocalAdjustment(numValue);
                  onDataSync(record.key, 'adjustment_quantity', numValue);
                }}
                style={{ width: '100%' }}
                size="small"
                step={1}
                controls={false}
                placeholder="0"
              />
            </div>
          </Col>
          <Col span={8}>
            <div style={{ textAlign: 'center', padding: 8, backgroundColor: '#f6ffed', borderRadius: 4 }}>
              <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>Tồn kho mới</div>
              <div style={{ 
                fontWeight: 'bold',
                color: (localAdjustment || 0) !== 0 ? 
                  ((localAdjustment || 0) > 0 ? '#52c41a' : '#ff4d4f') : '#333'
              }}>
                {formatNumber(record.current_stock + (localAdjustment || 0))}
              </div>
            </div>
          </Col>
        </Row>

        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Lý do điều chỉnh:</div>
          <Input
            value={localReason}
            onChange={(e) => setLocalReason(e.target.value)}
            onBlur={(e) => onDataSync(record.key, 'reason', e.target.value)}
            placeholder="Nhập lý do..."
            disabled={(localAdjustment || 0) === 0}
            size="small"
          />
        </div>

        <div style={{ textAlign: 'center' }}>
          <Tag color={statusInfo.color} style={{ margin: 0 }}>
            {statusInfo.text}
          </Tag>
        </div>
      </Card>
    );
  });

  const columns = [
    {
      title: 'Sản phẩm & Biến thể',
      key: 'product',
      fixed: 'left',
      width: 350,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold', color: '#1890ff' }}>{record.product_name}</div>
          {record.variant_name && record.variant_name !== record.product_name && (
            <div style={{ color: '#333', fontSize: '13px', marginTop: '2px' }}>
              📦 {record.variant_name}
            </div>
          )}
          <div style={{ color: '#999', fontSize: '11px', marginTop: '2px' }}>
            SKU: {record.sku} | ID: {record.variant_id}
          </div>
        </div>
      ),
    },
    {
      title: 'Tồn kho hiện tại',
      key: 'current_stock',
      width: 120,
      render: (_, record) => (
        <div style={{ textAlign: 'center', fontSize: '14px', fontWeight: 'bold' }}>
          {formatNumber(record.current_stock)}
        </div>
      ),
    },
    {
      title: 'Điều chỉnh',
      key: 'adjustment_quantity',
      width: 120,
      render: (_, record) => (
        <InputNumber
          value={record.adjustment_quantity}
          onChange={(value) => handleStockChange(record.key, 'adjustment_quantity', value || 0)}
          style={{ width: '100%' }}
          placeholder="+ tăng, - giảm"
          step={1}
        />
      ),
    },
    {
      title: 'Tồn kho mới',
      key: 'new_stock',
      width: 120,
      render: (_, record) => (
        <div 
          style={{ 
            textAlign: 'center', 
            fontSize: '14px', 
            fontWeight: 'bold',
            color: record.adjustment_quantity !== 0 ? 
              (record.adjustment_quantity > 0 ? '#52c41a' : '#ff4d4f') : '#333'
          }}
        >
          {formatNumber(record.new_stock)}
        </div>
      ),
    },
    {
      title: 'Lý do điều chỉnh',
      key: 'reason',
      width: 200,
      render: (_, record) => (
        <Input
          value={record.reason}
          onChange={(e) => handleStockChange(record.key, 'reason', e.target.value)}
          placeholder="Nhập lý do..."
          disabled={record.adjustment_quantity === 0}
        />
      ),
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 100,
      render: (_, record) => {
        if (record.adjustment_quantity === 0) {
          return <div style={{ color: '#999' }}>Không đổi</div>;
        }
        if (!record.reason.trim()) {
          return <div style={{ color: '#ff4d4f' }}>Thiếu lý do</div>;
        }
        return <div style={{ color: '#52c41a' }}>Sẵn sàng</div>;
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/stock/list')}
            >
              Quay lại
            </Button>
            <Title level={3} style={{ margin: 0 }}>
              Điều chỉnh tồn kho theo biến thể sản phẩm
            </Title>
          </Space>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Card title="Thông tin chung" size="small" style={{ marginBottom: 24 }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <Form.Item
                  name="adjustment_date"
                  label="Ngày điều chỉnh"
                  initialValue={new Date().toLocaleDateString('vi-VN')}
                >
                  <Input disabled />
                </Form.Item>
              </Col>
              <Col xs={24} lg={12}>
                <Form.Item name="adjustment_type" label="Loại điều chỉnh">
                  <Select placeholder="Chọn loại điều chỉnh">
                    <Option value="manual">Điều chỉnh thủ công</Option>
                    <Option value="inventory">Kiểm kê</Option>
                    <Option value="correction">Điều chỉnh sai số</Option>
                    <Option value="damaged">Hàng hỏng/mất</Option>
                    <Option value="other">Khác</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item name="general_reason" label="Lý do chung (tùy chọn)">
                  <TextArea 
                    rows={3} 
                    placeholder="Nhập lý do chung cho việc điều chỉnh này..."
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card 
            title={`Danh sách tất cả biến thể sản phẩm (${filteredStockData.length})`}
            size="small"
          >
            {/* Tìm kiếm sản phẩm */}
            <Row style={{ marginBottom: 16 }}>
              <Col xs={24} md={12}>
                <Input
                  placeholder="Tìm kiếm theo tên sản phẩm, biến thể hoặc SKU..."
                  onChange={(e) => handleSearch(e.target.value)}
                  allowClear
                />
              </Col>
              <Col xs={24} md={12} style={{ textAlign: isMobile ? 'left' : 'right', paddingTop: isMobile ? 8 : 0 }}>
                <span style={{ color: '#666' }}>
                  📦 {filteredStockData.length} biến thể / {stockData.length} tổng cộng
                </span>
              </Col>
            </Row>

            {isMobile ? (
              // Mobile: Card Layout
              <div 
                className="stock-scroll-container"
                style={{ maxHeight: '60vh', overflowY: 'auto' }}
              >
                {filteredStockData.length === 0 ? (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: 40,
                    color: '#999'
                  }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>📦</div>
                    <div>Không có biến thể nào</div>
                  </div>
                ) : (
                  <>
                    <div style={{ 
                      marginBottom: 12,
                      padding: '8px 12px',
                      backgroundColor: '#f0f9ff',
                      borderRadius: 6,
                      fontSize: 12,
                      color: '#1890ff'
                    }}>
                      📱 Hiển thị {filteredStockData.length} biến thể
                    </div>
                    {filteredStockData.map((record) => (
                      <MobileVariantCard 
                        key={record.key} 
                        record={record} 
                        onInputChange={handleInputChange}
                        onDataSync={syncDataUpdate}
                      />
                    ))}
                  </>
                )}
              </div>
            ) : (
              // Desktop: Table Layout
              <Table
                columns={columns}
                dataSource={filteredStockData}
                pagination={{
                  pageSize: 50,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} biến thể`,
                }}
                rowKey="key"
                loading={loading}
                scroll={{ x: 1200, y: 600 }}
                size="small"
                rowClassName={(record) => record.changed ? 'ant-table-row-selected' : ''}
              />
            )}
          </Card>

          <Divider />

          {/* Tổng kết */}
          {stockData.filter(item => item.changed && item.adjustment_quantity !== 0).length > 0 && (
            <Card title="Tổng kết điều chỉnh" size="small" style={{ marginBottom: 24 }}>
              <Row gutter={[16, 16]}>
                <Col xs={12} sm={6}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 'bold', color: '#1890ff' }}>
                      {stockData.filter(item => item.changed && item.adjustment_quantity !== 0).length}
                    </div>
                    <div style={{ color: '#666', fontSize: isMobile ? '11px' : '14px' }}>Biến thể điều chỉnh</div>
                  </div>
                </Col>
                <Col xs={12} sm={6}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 'bold', color: '#52c41a' }}>
                      {formatNumber(stockData.reduce((sum, item) => 
                        sum + (item.adjustment_quantity > 0 ? item.adjustment_quantity : 0), 0))}
                    </div>
                    <div style={{ color: '#666', fontSize: isMobile ? '11px' : '14px' }}>Tổng tăng</div>
                  </div>
                </Col>
                <Col xs={12} sm={6}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 'bold', color: '#ff4d4f' }}>
                      {formatNumber(Math.abs(stockData.reduce((sum, item) => 
                        sum + (item.adjustment_quantity < 0 ? item.adjustment_quantity : 0), 0)))}
                    </div>
                    <div style={{ color: '#666', fontSize: isMobile ? '11px' : '14px' }}>Tổng giảm</div>
                  </div>
                </Col>
                <Col xs={12} sm={6}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 'bold', color: '#722ed1' }}>
                      {stockData.filter(item => item.changed && item.adjustment_quantity !== 0 && item.reason.trim()).length}
                    </div>
                    <div style={{ color: '#666', fontSize: isMobile ? '11px' : '14px' }}>Sẵn sàng thực hiện</div>
                  </div>
                </Col>
              </Row>
            </Card>
          )}

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => navigate('/stock/list')}>
                Hủy
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
                disabled={stockData.filter(item => item.changed && item.adjustment_quantity !== 0 && item.reason.trim()).length === 0}
              >
                Thực hiện điều chỉnh
              </Button>
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default StockForm;
